// Login Form Component

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { loginUser, clearError } from '../../redux/slices/authSlice';
import { Input, Button, Alert } from '../common/index.js';
import { AUTH_STATUS } from '../../constants/app.constants';

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Clear any stale error from a previous session on mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(
        loginUser({
          username: formData.username,
          password: formData.password,
        })
      ).unwrap();

      // Land on Today — what needs the admin now, not the charts
      navigate('/today');
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
      {error && <Alert type="error" message={error} />}

      <Input
        label="Username"
        type="text"
        name="username"
        placeholder="Enter your username"
        value={formData.username}
        onChange={handleInputChange}
        error={validationErrors.username}
        required
        disabled={status === AUTH_STATUS.LOADING}
      />

      <div className="w-full">
        <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">
          Password
          <span className="text-rose-500 ml-1">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            disabled={status === AUTH_STATUS.LOADING}
            autoComplete="new-password"
          className={`w-full px-4 py-3 pr-12 border rounded-xl text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 ${
              validationErrors.password
                ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
            } ${status === AUTH_STATUS.LOADING ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            disabled={status === AUTH_STATUS.LOADING}
          >
            {showPassword ? (
              <FiEyeOff className="w-5 h-5" />
            ) : (
              <FiEye className="w-5 h-5" />
            )}
          </button>
        </div>
        {validationErrors.password && (
          <p className="text-rose-500 text-xs mt-1.5 font-medium">{validationErrors.password}</p>
        )}
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={status === AUTH_STATUS.LOADING}
        className="w-full"
      >
        Sign In
      </Button>

      <div className="text-center">
        <Link
          to="/forgot-password"
          className="text-sm text-violet-600 hover:text-violet-700 font-medium transition-colors"
        >
          Forgot Password?
        </Link>
      </div>

    </form>
  );
};

export default LoginForm;
